// Shared report container — handles loading, error, empty state, header,
// "Back to Reports" link, "Export CSV" button. Children render the actual table.

import React from "react";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const url = process.env.REACT_APP_BASE_URL;
const isDevelopment = process.env.NODE_ENV === "development";
const host = `${window.location.protocol}//${window.location.host}`;
const apiBase = isDevelopment ? `${url}/api/v1` : `${host}/api/v1`;

const ReportShell = ({ title, count, onExport, loading, error, children }) => {
  const navigate = useNavigate();
  return (
    <div className="container-fluid p-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <Button
            variant="link"
            className="p-0 mb-1"
            onClick={() => navigate("/reports")}
            style={{ textDecoration: "none" }}
          >
            ← Back to Reports
          </Button>
          <h4 className="mb-0">{title}</h4>
          {count !== undefined && (
            <small className="text-muted">{count} {count === 1 ? "row" : "rows"}</small>
          )}
        </div>
        <Button variant="outline-primary" size="sm" onClick={onExport} disabled={loading}>
          Export CSV
        </Button>
      </div>

      {loading && (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {!loading && !error && children}
    </div>
  );
};

// Helpers: open a CSV export URL by name. If a token-bearing fetch is required,
// we hit the URL via fetch + Authorization header, then trigger a blob download.
export const downloadReportCsv = async (name, queryString = "") => {
  const path = `/reports/${name}/export/${queryString}`;
  const fullUrl = `${apiBase}${path}`;
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";

  try {
    const resp = await fetch(fullUrl, {
      headers: token ? { Authorization: token } : {},
    });
    if (!resp.ok) {
      throw new Error(`HTTP ${resp.status}`);
    }
    const blob = await resp.blob();
    const dispo = resp.headers.get("Content-Disposition") || "";
    const match = dispo.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `${name}_report.csv`;
    const dlUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = dlUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(dlUrl);
  } catch (err) {
    alert(`CSV export failed: ${err.message}`);
  }
};

// Format a value for display: empty/null -> dash; numbers stringified.
export const display = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  return v;
};

// INR-formatted number
export const fmtINR = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (isNaN(n)) return v;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

export default ReportShell;
